import { TFunction } from 'client/i18n/hooks/useT';
import { EpsiodeItemDashboard } from 'client/screens/AnalyticsDashboardScreen/types';
import { AnchorClientRoutes } from '../AnchorClientRoutes';
import { getIsOnePublished } from '../Episode';
import * as ServerRenderingUtils from '../../../helpers/serverRenderingUtils.js';
import { Episode, ImportedPodcast, Podcast } from '../../types';
import { ActivationLifeCycleState } from '../AnchorAPI/v3/sponsor/fetchSponsorships';

const getIsMinimallySetup = (podcast: Podcast): boolean =>
  Boolean(
    podcast && podcast.name && podcast.description && podcast.iTunesCategory
  );

const getIsAnImportedPodcast = (podcast: Podcast): boolean =>
  podcast.kind === 'importedPodcast';

const getWasDistributionActionTaken = (podcastStatus: string): boolean =>
  podcastStatus !== 'noActionTaken';

const getIsRedirectionComplete = (podcast?: ImportedPodcast): boolean =>
  podcast
    ? podcast.redirectStatus === 'redirected' ||
      podcast.redirectStatus === 'ignored'
    : false;

// Was sponsorships action taken?
const getHasSponsorshipsBeenActivatedOrDeactived = (
  podcast: Podcast
): boolean =>
  podcast.sponsorshipsActivationStatus === 'activated' ||
  podcast.sponsorshipsActivationStatus === 'deactivated';

const getIsMonetizationSetup = ({
  activationLifeCycleState,
  isPaywallsSetup,
  hasSupportersEnabled,
}: {
  activationLifeCycleState?: ActivationLifeCycleState;
  isPaywallsSetup: boolean;
  hasSupportersEnabled: boolean;
}): boolean =>
  Boolean(
    activationLifeCycleState === 'activated' ||
      hasSupportersEnabled ||
      isPaywallsSetup
  );

const getIsSponsorshipsActivated = (sponsorshipsActivationStatus: string) =>
  sponsorshipsActivationStatus === 'activated';

const getCurrentlyAvailablePlatformsCount = (
  podcast: Podcast,
  episodes: Episode[]
): number =>
  getIsOnePublished(episodes) ? podcast.listeningPlatforms.length : 0;

const getHasCreatedAnEpisode = (
  episodes: Episode[] | EpsiodeItemDashboard[]
): boolean => episodes.length > 0;

const getHTMLEmbedCode = (podcast: Podcast): string => {
  const embedUrl = `${ServerRenderingUtils.getBaseUrl()}${AnchorClientRoutes.getUrlPathForPodcastEmbed(
    podcast
  )}`;
  return `<iframe src="${embedUrl}" height="102px" width="400px" frameborder="0" scrolling="no"></iframe>`;
};

const getFacebookShareUrl = (podcast: Podcast): string => {
  const podcastPublicUrl = `${ServerRenderingUtils.getBaseUrl()}${AnchorClientRoutes.getUrlPathForPodcastPublicProfile(
    podcast
  )}`;
  return `https://www.facebook.com/dialog/share?app_id=446611785530020&href=${podcastPublicUrl}&redirect_uri=${podcastPublicUrl}`;
};

const getTwitterShareUrl = (podcast: Podcast, t: TFunction) => {
  const podcastPublicUrl = `${ServerRenderingUtils.getBaseUrl()}${AnchorClientRoutes.getUrlPathForPodcastPublicProfile(
    podcast
  )}`;
  const shareText = t(
    'Podcast.TwitterShareUrlText',
    'Listen to {podcastName} on Anchor',
    'Twitter share text',
    {
      podcastName: podcast.name
        ? podcast.name
        : t('UntitledAudioLabel', 'Untitled', 'Untitled audio label'),
    }
  );
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    shareText
  )} ${podcastPublicUrl}`;
};

const getCoverartUrl = (
  podcast: Podcast,
  size: 'normalSize' | 'fullSize'
): string | null => {
  switch (size) {
    case 'normalSize':
      return podcast.coverartImage && podcast.coverartImage.normalSize
        ? podcast.coverartImage.normalSize.url
        : null;
    case 'fullSize':
      return podcast.coverartImage && podcast.coverartImage.fullSize
        ? podcast.coverartImage.fullSize.url
        : null;
    default:
      return null;
  }
};

export {
  getIsRedirectionComplete,
  getIsMinimallySetup,
  getHasCreatedAnEpisode,
  getIsAnImportedPodcast,
  getIsMonetizationSetup,
  getWasDistributionActionTaken,
  getHTMLEmbedCode,
  getFacebookShareUrl,
  getTwitterShareUrl,
  getCoverartUrl,
  getCurrentlyAvailablePlatformsCount,
  getHasSponsorshipsBeenActivatedOrDeactived,
  getIsSponsorshipsActivated,
};
